import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchPeople, createPerson } from '../api/people';
import { fetchDigestSchedule, updateDigestSchedule, sendDigestNow } from '../api/digestSchedule';
import type { Person, PersonType } from '../types/people';

const PERSON_TYPE: PersonType = 'founder';
const TITLE = 'Founders';
const BREADCRUMB = 'Founders';

export default function PeopleFoundersPage() {
  const navigate = useNavigate();
  const [people, setPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', email: '' });
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState('');
  const [digestSchedule, setDigestSchedule] = useState({ day_of_week: 0, hour: 23 });
  const [digestScheduleSaving, setDigestScheduleSaving] = useState(false);
  const [digestSendNowLoading, setDigestSendNowLoading] = useState(false);
  const [digestMessage, setDigestMessage] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    fetchPeople(PERSON_TYPE)
      .then(setPeople)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    fetchDigestSchedule().then(setDigestSchedule).catch(() => {});
  }, []);

  const handleSaveDigestSchedule = useCallback(async () => {
    setDigestScheduleSaving(true);
    setDigestMessage('');
    try {
      const updated = await updateDigestSchedule(digestSchedule);
      setDigestSchedule(updated);
      setDigestMessage('Schedule saved.');
    } catch {
      setDigestMessage('Failed to save schedule.');
    } finally {
      setDigestScheduleSaving(false);
    }
  }, [digestSchedule]);

  const handleSendDigestNow = useCallback(async () => {
    setDigestSendNowLoading(true);
    setDigestMessage('');
    try {
      const result = await sendDigestNow();
      if (result.sent > 0) {
        setDigestMessage(`Digest sent to ${result.sent} ${result.sent === 1 ? 'person' : 'people'}.`);
      } else if (result.reason === 'not_configured') {
        setDigestMessage('Email not configured on the server. Set SENDGRID_API_KEY and DIGEST_FROM_EMAIL in production.');
      } else if (result.reason === 'no_recipients') {
        setDigestMessage('No emails sent: no assignees have tasks due next week. Add due dates (Mon–Sun) and ensure people have emails.');
      } else {
        setDigestMessage('Digest sent (0 recipients).');
      }
    } catch {
      setDigestMessage('Failed to send digest.');
    } finally {
      setDigestSendNowLoading(false);
    }
  }, []);

  const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({ value: i, label: i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM` }));

  const handleAddSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const name = addForm.name.trim();
      if (!name) {
        setAddError('Name is required');
        return;
      }
      setAddSaving(true);
      setAddError('');
      try {
        const created = await createPerson({
          name,
          email: addForm.email.trim() || null,
          person_type: PERSON_TYPE,
        });
        setPeople((prev) => [...prev, created]);
        setAddForm({ name: '', email: '' });
        setAddModalOpen(false);
        navigate(`/people/${created.id}`);
      } catch (err) {
        setAddError(err instanceof Error ? err.message : 'Failed to add person');
      } finally {
        setAddSaving(false);
      }
    },
    [addForm, navigate]
  );

  if (loading) {
    return <div>Loading…</div>;
  }
  if (error) {
    return <div style={{ color: 'var(--color-coral)' }}>{error}</div>;
  }

  return (
    <>
      <nav style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginBottom: '8px' }}>
        <Link to="/people/founders" style={{ color: 'var(--color-blue-dark)' }}>
          People
        </Link>
        {' / '}
        <span>{BREADCRUMB}</span>
      </nav>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: 700, margin: '0 0 8px' }}>{TITLE}</h1>
          <p style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
            Manage founders and view tasks assigned to each.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setAddError('');
            setAddForm({ name: '', email: '' });
            setAddModalOpen(true);
          }}
          style={{
            padding: '10px 20px',
            background: 'var(--color-blue-dark)',
            color: 'white',
            border: 'none',
            borderRadius: 'var(--radius)',
            fontWeight: 600,
            cursor: 'pointer',
            fontSize: '14px',
          }}
        >
          Add founder
        </button>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '20px',
        }}
      >
        {people.map((person) => (
          <Link
            key={person.id}
            to={`/people/${person.id}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              padding: '20px',
              background: 'white',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius)',
              textDecoration: 'none',
              color: 'inherit',
              transition: 'box-shadow 0.2s',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <img
              src={person.avatar_url ?? undefined}
              alt=""
              style={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                objectFit: 'cover',
                background: 'var(--color-todo-bg)',
              }}
            />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: '16px', marginBottom: '2px' }}>{person.name}</div>
              {person.email && (
                <div style={{ fontSize: '13px', color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {person.email}
                </div>
              )}
            </div>
          </Link>
        ))}
      </div>

      <div
        style={{
          background: 'white',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius)',
          padding: '20px',
          marginTop: '24px',
          maxWidth: 480,
        }}
      >
        <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 8px' }}>Weekly digest</h2>
        <p style={{ color: 'var(--color-text-secondary)', margin: '0 0 16px', fontSize: '14px' }}>
          Send assignees an email with tasks due next week. Set when it runs and optionally send now.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end', marginBottom: '12px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>Day</label>
            <select
              value={digestSchedule.day_of_week}
              onChange={(e) => setDigestSchedule((s) => ({ ...s, day_of_week: Number(e.target.value) }))}
              style={{ padding: '8px 10px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', minWidth: 120 }}
            >
              {DAY_NAMES.map((name, i) => (
                <option key={i} value={i}>{name}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>Hour</label>
            <select
              value={digestSchedule.hour}
              onChange={(e) => setDigestSchedule((s) => ({ ...s, hour: Number(e.target.value) }))}
              style={{ padding: '8px 10px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', minWidth: 100 }}
            >
              {HOUR_OPTIONS.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={handleSaveDigestSchedule}
            disabled={digestScheduleSaving}
            style={{
              padding: '8px 16px',
              background: 'var(--color-blue-dark)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius)',
              fontWeight: 500,
              cursor: digestScheduleSaving ? 'not-allowed' : 'pointer',
              fontSize: '14px',
            }}
          >
            {digestScheduleSaving ? 'Saving…' : 'Save schedule'}
          </button>
          <button
            type="button"
            onClick={handleSendDigestNow}
            disabled={digestSendNowLoading}
            style={{
              padding: '8px 16px',
              background: 'var(--color-green, #0d9488)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius)',
              fontWeight: 500,
              cursor: digestSendNowLoading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
            }}
          >
            {digestSendNowLoading ? 'Sending…' : 'Send now'}
          </button>
        </div>
        {digestMessage && (
          <div style={{ fontSize: '13px', color: digestMessage.startsWith('F') ? 'var(--color-coral)' : 'var(--color-text-secondary)' }}>
            {digestMessage}
          </div>
        )}
      </div>

      {addModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
          }}
          onClick={() => !addSaving && setAddModalOpen(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              width: '100%',
              maxWidth: 400,
              boxShadow: '0 18px 45px rgba(15, 23, 42, 0.18)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 16px' }}>Add founder</h2>
            <form onSubmit={handleAddSubmit}>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Name *</label>
                <input
                  value={addForm.name}
                  onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Alex"
                  autoFocus
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)' }}
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Email</label>
                <input
                  type="email"
                  value={addForm.email}
                  onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="alex@example.com"
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)' }}
                />
              </div>
              {addError && <div style={{ color: 'var(--color-coral)', fontSize: '13px', marginBottom: '12px' }}>{addError}</div>}
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => !addSaving && setAddModalOpen(false)}
                  style={{
                    padding: '8px 16px',
                    background: 'transparent',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius)',
                    cursor: addSaving ? 'not-allowed' : 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addSaving || !addForm.name.trim()}
                  style={{
                    padding: '8px 16px',
                    background: 'var(--color-blue-dark)',
                    color: 'white',
                    border: 'none',
                    borderRadius: 'var(--radius)',
                    fontWeight: 500,
                    cursor: addSaving ? 'not-allowed' : 'pointer',
                  }}
                >
                  {addSaving ? 'Adding…' : 'Add founder'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
